import React, { useEffect, useRef } from "react";

import { ENTRY_DATA_SECTION_KEYS } from "@frontend/common";

import { DealLaScSection } from "@/types/dealLaSc.types";
import GroupShellTitleDescriptionContainer from "@FillInTableContainer/shared/components/GroupShellTitleDescriptionContainer/GroupShellTitleDescriptionContainer";
import { useHandleTableCreatorGroupShellDrag } from "@FillInTableContainer/shared/hooks/useHandleTableCreatorGroupShellDrag";
import { TableCreatorSubGroupGroupShellList } from "@FillInTableContainer/shared/types/tableCreator.types";
import { getGroupShellTitle } from "@FillInTableContainer/shared/utils/getTextFromData";
import IconXMark from "@images/fillInTable/tableCreator/icon-x-mark-white.svg";
import { newArrayToCountValue } from "@utils/newArrayToCountValue";

import DealLaScRightsScopeGroupShell from "./components/DealLaScRightsScopeGroupShell/DealLaScRightsScopeGroupShell";
import * as S from "./DealLaScRightsScopeGroupShellSection.style";
import useDealLaScRightsScopeTableCreatorGroupShellSection from "./hooks/useDealLaScRightsScopeTableCreatorGroupShellSection";

interface Props {
  groupTableKey: DealLaScSection;
  groupTableIndex: number;
  groupShellSectionKey: string | null;
  groupShellList: TableCreatorSubGroupGroupShellList;
  tableKey: string | null;
  tableIndex: number;
  groupShellSectionIndex: number;
}

const ARTICLE_SECTION = ENTRY_DATA_SECTION_KEYS.dealsLaSc;

const DealLaScRightsScopeGroupShellSection = ({
  groupShellSectionIndex,
  groupShellSectionKey,
  groupTableIndex,
  groupTableKey,
  groupShellList,
  tableIndex,
  tableKey,
}: Props) => {
  const groupShellSectionProps = {
    groupShellSectionIndex,
    groupShellSectionKey,
    groupTableIndex,
    groupTableKey,
    tableIndex,
    tableKey,
  };

  const {
    numberOfGroupShellCount,
    handleFocusGroupShellSection,
    handleClickToClearGroupShellSection,
    handleDefaultFocus,
    isGroupShellSectionFocus,
    isClearGroupShellSection,
  } = useDealLaScRightsScopeTableCreatorGroupShellSection(
    groupShellSectionProps,
  );

  const {
    handleGroupShellDragEnd,
    handleGroupShellDragOn,
    handleGroupShellDragOver,
    handleGroupShellDragStart,
    groupShellDraggedIndexInfo,
    isDraggingGroupShell,
  } = useHandleTableCreatorGroupShellDrag(ARTICLE_SECTION, tableIndex);

  const titleRef = useRef<HTMLDivElement>(null);
  const clearButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleCheckClickOutside = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-dropdown="list-box"]'))
        return;
      if (isGroupShellSectionFocus) {
        const target = e.target as HTMLElement;
        const clickSelectButton = clearButtonRef.current?.contains(target);
        const clickGroupShellTitle = titleRef.current?.contains(target);

        if (clickSelectButton || clickGroupShellTitle) return;
        if (target.dataset.button) return;

        handleDefaultFocus();
      }
    };

    window.addEventListener("click", handleCheckClickOutside);

    return () => window.removeEventListener("click", handleCheckClickOutside);
  }, [handleDefaultFocus, isGroupShellSectionFocus]);

  return (
    <S.GroupShellSectionWrapper
      key={`groupShellList-${String(groupShellSectionIndex)}`}
      isFocused={isGroupShellSectionFocus}
    >
      {isClearGroupShellSection && (
        <S.FixedDeleteButtonWrapper
          isShellTitle={
            !!getGroupShellTitle(ARTICLE_SECTION, groupShellSectionKey)
          }
          ref={clearButtonRef}
        >
          <S.DeleteGroupShellSectionButtonWrapper>
            <S.DeleteGroupShellSectionButton
              data-button="delete-button"
              onClick={handleClickToClearGroupShellSection}
            >
              <img src={IconXMark} alt="button for clear Row" />
              Clear
            </S.DeleteGroupShellSectionButton>
          </S.DeleteGroupShellSectionButtonWrapper>
        </S.FixedDeleteButtonWrapper>
      )}
      <div ref={titleRef}>
        <GroupShellTitleDescriptionContainer
          mainGroupShellTitle={getGroupShellTitle(
            ARTICLE_SECTION,
            groupShellSectionKey,
          )}
          onClickHandler={handleFocusGroupShellSection}
        />
      </div>
      {groupShellList.map(({ groupShellKey, subGroupShellList }) => (
        <React.Fragment
          key={`groupShellList-${String(groupShellSectionIndex)}`}
        >
          {newArrayToCountValue(numberOfGroupShellCount(groupShellKey)).map(
            (_, groupShellIndex) => (
              <S.DraggableGroupShellWrapper
                key={`group-shell-${String(groupTableIndex)}-${String(
                  tableIndex,
                )}-${String(groupShellIndex)}`}
                isTargetDraggingTable={
                  isDraggingGroupShell &&
                  groupShellDraggedIndexInfo[0] !== groupShellIndex
                }
                onDragOver={(e) => handleGroupShellDragOver(e, groupShellIndex)}
              >
                <DealLaScRightsScopeGroupShell
                  key={`group-shell-${String(groupTableIndex)}-${String(
                    tableIndex,
                  )}-${String(groupShellIndex)}`}
                  subGroupShellList={subGroupShellList}
                  groupTableKey={groupTableKey}
                  groupTableIndex={groupTableIndex}
                  groupShellSectionIndex={groupShellSectionIndex}
                  groupShellSectionKey={groupShellSectionKey}
                  tableKey={tableKey}
                  tableIndex={tableIndex}
                  groupShellKey={groupShellKey}
                  groupShellIndex={groupShellIndex}
                  groupShellCount={numberOfGroupShellCount(groupShellKey)}
                  handleDragEnd={handleGroupShellDragEnd}
                  handleDragOn={handleGroupShellDragOn}
                  handleDragStart={handleGroupShellDragStart}
                  isDraggableGroupShell={isDraggingGroupShell}
                  isFocusGroupShellContainer={isGroupShellSectionFocus}
                />
              </S.DraggableGroupShellWrapper>
            ),
          )}
        </React.Fragment>
      ))}
    </S.GroupShellSectionWrapper>
  );
};

export default DealLaScRightsScopeGroupShellSection;
